// Define required interfaces locally to avoid dependencies
// VideoModel interface removed as it was unused

export interface CreditCalculationParams {
  modelId: string;
  duration: number; // in seconds
  width: number;
  height: number;
  outputFormat: string;
  withAudio?: boolean;
}

interface ModelCostConfig {
  // Base cost for standard duration in cents (USD) - some models use fixed pricing per video
  baseCost: number;
  // Whether pricing is per second or per video (fixed price)
  pricingModel: 'per_second' | 'per_video';
  // Standard duration in seconds this price applies to (for per_video pricing)
  standardDuration?: number;
  // Extra cost per additional second beyond standard duration (for per_video pricing)
  extraSecondCost?: number;
  // Multipliers for different resolutions
  resolutionMultipliers: Record<string, number>;
  // Multiplier when audio is enabled
  audioMultiplier: number;
}

// Define the cost configurations for each model based on Runware's official pricing
// These are the RAW costs from Runware before our markup
// Prices verified from https://runware.ai/pricing (July 2025)
const MODEL_COSTS: Record<string, ModelCostConfig> = {
  'kling-2.1-master-pro': {
    baseCost: 250, // $2.50 base cost for standard video
    pricingModel: 'per_video',
    standardDuration: 5, // 5 second standard duration
    extraSecondCost: 50, // $0.50 per additional second
    resolutionMultipliers: {
      'sd': 1.0,  // 512x512 (standard definition)
      'hd': 1.5,  // 720p
      'fullhd': 2.0, // 1080p
    },
    audioMultiplier: 1.25, // 25% more when audio is enabled
  },
  'kling-2.1-standard': {
    baseCost: 150, // $1.50 base cost for standard video
    pricingModel: 'per_video',
    standardDuration: 5, // 5 second standard duration
    extraSecondCost: 30, // $0.30 per additional second
    resolutionMultipliers: {
      'sd': 1.0,
      'hd': 1.5,
      'fullhd': 2.0,
    },
    audioMultiplier: 1.25,
  },
  'minimax': {
    baseCost: 100, // $1.00 base cost for standard video
    pricingModel: 'per_video',
    standardDuration: 5, // 5 second standard duration
    extraSecondCost: 20, // $0.20 per additional second
    resolutionMultipliers: {
      'sd': 1.0,
      'hd': 1.2,
    },
    audioMultiplier: 1.2,
  },
  'haliou-2': {
    baseCost: 180, // $1.80 base cost for standard video
    pricingModel: 'per_video',
    standardDuration: 5, // 5 second standard duration
    extraSecondCost: 36, // $0.36 per additional second
    resolutionMultipliers: {
      'sd': 1.0,
      'hd': 1.5,
    },
    audioMultiplier: 1.3,
  },
  'google-veo3': {
    baseCost: 400, // $4.00 base cost for 8 second video
    pricingModel: 'per_video',
    standardDuration: 8, // 8 second standard duration
    resolutionMultipliers: {
      'sd': 1.0,
      'hd': 1.5,
      'fullhd': 2.0,
    },
    audioMultiplier: 1.25,
  },
  'google-veo3-with-sound': {
    baseCost: 500, // $5.00 base cost for 8 second video with sound
    pricingModel: 'per_video',
    standardDuration: 8, // 8 second standard duration
    resolutionMultipliers: {
      'sd': 1.0,
      'hd': 1.5,
      'fullhd': 2.0,
    },
    audioMultiplier: 1.0, // Already includes audio
  },
};

// Map UI model IDs to internal cost model IDs
// This mapping allows us to use different ID formats in the UI vs internal cost calculations
const MODEL_ID_MAP: Record<string, string> = {
  // Format: 'UI_MODEL_ID': 'INTERNAL_COST_MODEL_ID'
  'klingai:5@3': 'kling-2.1-master-pro',
  'hailuo:02@1': 'haliou-2',
  'google:veo3@1': 'google-veo3',
  'vidu:q1@1': 'kling-2.1-standard', // Map Vidu to Kling standard as a substitute
  'pixverse:4.5@1': 'minimax' // Map PixVerse to Minimax as a substitute
};

/**
 * Maps a UI model ID to the corresponding internal cost model ID
 * @param uiModelId The model ID used in the UI
 * @returns The corresponding internal cost model ID
 */
const mapModelId = (uiModelId: string): string => {
  // Return the mapped ID if it exists, otherwise return the original ID
  return MODEL_ID_MAP[uiModelId] || uiModelId;
};

// Credit pricing: 50 credits for £10 (approx $12.70)
// Therefore 1 credit = £0.20 = approx $0.254
// This gives us cents per credit: 25.4 cents per credit
const CREDIT_TO_USD_CENTS = 25.4;

// Profit margin (30% markup over Runware base costs)
const MARGIN_MULTIPLIER = 1.3;

// Helper to determine resolution category based on width/height
const getResolutionCategory = (width: number, height: number): string => {
  const pixelCount = width * height;
  
  if (pixelCount <= 512 * 512) {
    return 'sd';
  } else if (pixelCount <= 1280 * 720) {
    return 'hd';
  } else {
    return 'fullhd';
  }
};

/**
 * Returns the base cost per second for a given model
 * @param modelId The UI model ID
 * @returns The base cost per second in credits (without any multipliers)
 */
export const getBaseModelCost = (modelId: string): number => {
  // Map the UI model ID to internal cost model ID
  const internalModelId = mapModelId(modelId);
  
  // Get model config, default to kling-2.1-standard if model not found
  const modelConfig = MODEL_COSTS[internalModelId] || MODEL_COSTS['kling-2.1-standard'];
  
  // For per_video pricing, calculate cost per second based on standard duration
  const baseCost = modelConfig.pricingModel === 'per_second' 
    ? modelConfig.baseCost 
    : modelConfig.baseCost / (modelConfig.standardDuration || 5);
  
  // Convert raw cost in cents to credits with proper rounding
  // Note: We round up to ensure we cover our costs
  return Math.ceil((baseCost * MARGIN_MULTIPLIER) / CREDIT_TO_USD_CENTS);
};

/**
 * Calculate the estimated credit cost for a video generation
 * @param params The parameters for the video generation
 * @returns The estimated credit cost
 */
export const calculateVideoCredits = (params: CreditCalculationParams): number => {
  const { modelId, duration, width, height, withAudio } = params;
  
  // Map UI model ID to internal cost model ID
  const internalModelId = mapModelId(modelId);
  
  // Get the model cost config, default to kling-2.1-standard if model not found
  const modelConfig = MODEL_COSTS[internalModelId] || MODEL_COSTS['kling-2.1-standard'];
  
  // Determine resolution category based on width/height
  const resolutionCategory = getResolutionCategory(width, height);
  
  // Get resolution multiplier (default to 1.0 if not found)
  const resolutionMultiplier = modelConfig.resolutionMultipliers[resolutionCategory] || 1.0;
  
  // Apply audio multiplier if audio is enabled (and the model doesn't already include audio)
  const audioMultiplier = withAudio ? modelConfig.audioMultiplier : 1.0;
  
  // Calculate raw cost in cents based on pricing model
  let rawCostInCents: number;
  
  if (modelConfig.pricingModel === 'per_second') {
    // Per-second pricing model
    rawCostInCents = modelConfig.baseCost * duration;
  } else {
    // Per-video pricing model
    const standardDuration = modelConfig.standardDuration || 5;
    
    if (duration <= standardDuration) {
      // Standard duration or less
      rawCostInCents = modelConfig.baseCost;
    } else {
      // Additional cost for extra seconds
      const extraSeconds = duration - standardDuration;
      const extraCost = extraSeconds * (modelConfig.extraSecondCost || 0);
      rawCostInCents = modelConfig.baseCost + extraCost;
    }
  }
  
  // Apply resolution and audio multipliers
  rawCostInCents *= resolutionMultiplier * audioMultiplier;
  
  // Apply margin
  const costWithMargin = rawCostInCents * MARGIN_MULTIPLIER;
  
  // Convert to credits
  const creditsRequired = costWithMargin / CREDIT_TO_USD_CENTS;
  
  // Return rounded up to nearest whole credit
  return Math.ceil(creditsRequired);
};

/**
 * Estimates the cost of a video before generation based on model and parameters
 * Returns a formatted string for display to the user
 */
export const getEstimatedCreditCost = (params: CreditCalculationParams): string => {
  const credits = calculateVideoCredits(params);
  return `${credits} credit${credits !== 1 ? 's' : ''}`;
};

/**
 * Provides a detailed breakdown of the cost calculation
 * Useful for transparency and explaining costs to users
 */
export const getCreditCostBreakdown = (params: CreditCalculationParams): string => {
  const { modelId, duration, width, height, withAudio = false } = params;
  const credits = calculateVideoCredits(params);
  const resolutionCategory = getResolutionCategory(width, height);
  
  return `
    Model: ${modelId}
    Duration: ${duration} seconds
    Resolution: ${width}x${height} (${resolutionCategory})
    Audio: ${withAudio ? 'Yes' : 'No'}
    Total: ${credits} credit${credits !== 1 ? 's' : ''}
  `;
};

/**
 * Validates if a user has enough credits for a video generation
 */
export const hasEnoughCredits = (availableCredits: number, params: CreditCalculationParams): boolean => {
  const requiredCredits = calculateVideoCredits(params);
  return availableCredits >= requiredCredits;
};
